/**
 * Monster pockets of the 256 m plains (data/layouts/plains.ts `POCKETS`, lane C0-P1): every pocket's monsters set down
 * in its spawn rectangle when the World is made, round-robin over the pocket's monster list. Monsters respawn where
 * they were set down (the World's monster brain), so a pocket keeps its count.
 *
 * Pockets the World already fills are skipped: the wolves (K1) and bandits (K4) are the zone's own spawns
 * (data/zones.ts), the black-sword boss (K11) comes with his quest, the ape (K12) is the world boss on the arena
 * point. Pockets with no monster made yet (K5, K13) wait for Systems' monster table. Only on the 256 m plains: the 72 m
 * map is untouched. Draws come from the World's Rng, in pocket order.
 */
import { POCKETS, PLAINS_SIZE, type MonsterPocket } from '../data/layouts/plains';
import { randomFloorIn } from '../map';
import type { SystemHost, WorldSystem } from './types';

/** Pockets filled elsewhere (see above). */
export const POCKETS_FILLED_ELSEWHERE: ReadonlySet<string> = new Set(['K1', 'K4', 'K11', 'K12']);

/** The pockets this system fills. */
export const pocketsToSpawn = (): readonly MonsterPocket[] =>
  POCKETS.filter((p) => p.monsters.length && !POCKETS_FILLED_ELSEWHERE.has(p.id));

export class PocketsSystem implements WorldSystem {
  readonly name = 'pockets';
  constructor(private readonly h: SystemHost) {}

  setup(): void {
    const m = this.h.world.map;
    if (m.zone !== 'plains' || m.w !== PLAINS_SIZE || m.interior) return;
    for (const p of pocketsToSpawn())
      for (let k = 0; k < p.n; k++) {
        const at = randomFloorIn(m, p.box, () => this.h.random());
        this.h.spawnMonster(p.monsters[k % p.monsters.length]!, at.x, at.y);
      }
  }
}
